import { computed, linkedSignal, Signal } from '@angular/core';
import { apply, form, SchemaOrSchemaFn, SchemaPath, SchemaPathTree } from '@angular/forms/signals';
import { Ummeldung } from '../../../../generated/model/ummeldung.model';
import { Produktdaten } from '../../../../generated/model/produktdaten.model';

/**
 * Whether the Produkt step (1) has every required input: the Zählernummer, the Zählertyp, and for a
 * konventioneller Zähler either a Zählerstand or the "kein Zählerstand" tick.
 */
export function isProduktStepComplete(ummeldung: Ummeldung | null): boolean {
  const produktdaten = ummeldung?.produktdaten ?? [];
  return produktdaten.length > 0 && produktdaten.every(isProduktComplete);
}

function isProduktComplete(produkt: Produktdaten): boolean {
  if (!produkt.zaehlerNr?.trim()) {
    return false;
  }
  if (produkt.isSmartMeter === null || produkt.isSmartMeter === undefined) {
    return false;
  }
  return produkt.isSmartMeter || hasZaehlerstandOrNoZaehlerstandTick(produkt);
}

/** Whether a konventioneller Zähler has a Zählerstand or the "kein Zählerstand" tick (both captured in step 1). */
function hasZaehlerstandOrNoZaehlerstandTick(produkt: Produktdaten): boolean {
  return !!produkt.hasNoZaehlerstand || produkt.zaehlerstand != null;
}

/**
 * Whether the Ummeldedatum step (2) is complete. What that means depends on the Zählertyp:
 * - Smart Meter: the Ummeldedatum was picked manually in step 2.
 * - Konventioneller Zähler: nothing is entered in step 2, the date follows from step 1. So the step
 *   counts as complete once step 1 has a Zählerstand or the "kein Zählerstand" tick.
 * - No Zählertyp yet: incomplete.
 */
export function isDatumStepComplete(ummeldung: Ummeldung | null): boolean {
  const produkt = ummeldung?.produktdaten?.at(0);

  if (produkt?.isSmartMeter === true) {
    return !!ummeldung?.ummeldedatum;
  }
  if (produkt?.isSmartMeter === false) {
    return hasZaehlerstandOrNoZaehlerstandTick(produkt);
  }
  return false;
}

/**
 * Whether the draft passes every given schema, judged on a detached Signal Forms tree — so a step
 * can be checked against exactly its own rules without being on screen. Needs an injection context.
 */
export function isDraftValid<TFormModel>(draft: () => TFormModel, ...schemas: SchemaOrSchemaFn<TFormModel>[]): Signal<boolean> {
  const field = form(linkedSignal(draft), (path: SchemaPathTree<TFormModel>) => {
    schemas.forEach(stepSchema => apply(path as SchemaPath<TFormModel>, stepSchema));
  });
  return computed(() => !field().invalid());
}
